import { observable, configure, action } from 'mobx';
import util from 'preact-util';

configure({ enforceActions: 'always' });

const POSITION_KEY = 'keepspot.onThisDay.position';
const DENIED_KEY = 'keepspot.onThisDay.geolocationDenied';

function readJson(key) {
    try {
        const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (err) {
        return null;
    }
}

function writeJson(key, value) {
    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(key, JSON.stringify(value));
        }
    } catch (err) {
        // ignore
    }
}

class OnThisDayStore {
    @observable onThisDay = {};

    @observable quote = null;

    @observable isLoading = false;

    @observable lastLoadedKey = null;

    @observable weather = [];

    @observable weatherLocation = null;

    @observable weatherIsLoading = false;

    @action
    updateKeyValue(key, value) {
        this[key] = value;
    }

    @action
    async load({ month, day } = {}) {
        let url = '/api/onthisday/';
        let key = 'today';
        if (month && day) {
            const m = String(month).padStart(2, '0');
            const d = String(day).padStart(2, '0');
            url = `/api/onthisday/${m}/${d}`;
            key = `${m}-${d}`;
        }
        if (this.isLoading) return;
        if (this.lastLoadedKey === key && this.onThisDay?.monthDay) return;

        this.updateKeyValue('isLoading', true);
        try {
            const response = await util.fetchApi(url, { method: 'GET' });
            const data = response?.data || {};
            this.updateKeyValue('onThisDay', data.onThisDay || {});
            this.updateKeyValue('quote', data.quote || null);
            this.updateKeyValue('lastLoadedKey', key);
        } catch (err) {
            // Silent — the dashboard should degrade gracefully if the
            // Wikipedia cache is empty or the backend is unreachable.
            this.updateKeyValue('onThisDay', {});
        } finally {
            this.updateKeyValue('isLoading', false);
        }
    }

    // Resolve a usable { lat, lon, city? } without nagging the user.
    // Priority: cached precise position → IP fallback → one-time geolocation prompt.
    async resolvePosition() {
        const cached = readJson(POSITION_KEY);
        if (cached && typeof cached.lat === 'number' && typeof cached.lon === 'number') {
            return cached;
        }

        const denied = readJson(DENIED_KEY);
        if (denied || typeof navigator === 'undefined' || !navigator.geolocation) {
            return this.resolvePositionByIp();
        }

        try {
            const coords = await new Promise((resolve, reject) => {
                const timeoutId = setTimeout(() => reject(new Error('timeout')), 7000);
                navigator.geolocation.getCurrentPosition(
                    (pos) => { clearTimeout(timeoutId); resolve(pos.coords); },
                    (err) => { clearTimeout(timeoutId); reject(err); },
                    { enableHighAccuracy: false, maximumAge: 5 * 60 * 1000, timeout: 6000 },
                );
            });
            const position = {
                lat: coords.latitude,
                lon: coords.longitude,
                source: 'geolocation',
                ts: Date.now(),
            };
            writeJson(POSITION_KEY, position);
            return position;
        } catch (err) {
            writeJson(DENIED_KEY, { ts: Date.now() });
            return this.resolvePositionByIp();
        }
    }

    async resolvePositionByIp() {
        try {
            const response = await util.fetchApi('/api/public/location', { method: 'GET' });
            const loc = response?.data?.location || response?.location;
            const locStr = loc?.loc || '';
            const parts = locStr.split(',');
            if (parts.length !== 2) return null;
            const lat = parseFloat(parts[0]);
            const lon = parseFloat(parts[1]);
            if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
            const position = {
                lat,
                lon,
                city: loc?.city || null,
                source: 'ip',
                ts: Date.now(),
            };
            // Cache IP-based position too so we don't hit the endpoint every
            // visit; refreshed later when user precisely locates or clears storage.
            writeJson(POSITION_KEY, position);
            return position;
        } catch (err) {
            return null;
        }
    }

    @action
    async loadWeather() {
        if (this.weatherIsLoading) return;
        const position = await this.resolvePosition();
        if (!position) return;

        this.updateKeyValue('weatherIsLoading', true);
        try {
            const response = await util.fetchApi('/api/yr/public', { method: 'GET' }, {
                lat: position.lat,
                lon: position.lon,
            });
            if (response?.status === 200 && Array.isArray(response.data)) {
                this.updateKeyValue('weather', response.data);
                this.updateKeyValue('weatherLocation', position.city || null);
            }
        } catch (err) {
            // Silent — weather is nice-to-have.
        } finally {
            this.updateKeyValue('weatherIsLoading', false);
        }
    }
}

const onThisDayStore = new OnThisDayStore();
export default onThisDayStore;
